import { FRAME_SAFE_ERROR_KEY } from "./WrappedError"

import type { ChatKitResponseMetadata } from "../types"

const MISALIGNMENT_ERROR_CODE = "misalignment_policy_violation"
const MAX_ERROR_BODY_BYTES = 16 * 1024
const ERROR_BODY_TIMEOUT_MS = 1_000

export class HttpError extends Error {
  status?: number
  statusText?: string
  metadata?: ChatKitResponseMetadata
  declare code?: typeof MISALIGNMENT_ERROR_CODE

  constructor(
    message: string,
    res: Response | { status?: number; statusText?: string },
    metadata?: ChatKitResponseMetadata,
  ) {
    super(message)
    this.name = "HttpError"
    this.statusText = res.statusText
    this.status = res.status
    this.metadata = metadata
  }

  static async fromResponse(
    message: string,
    response: Response,
    metadata?: ChatKitResponseMetadata,
  ): Promise<HttpError> {
    const error = new HttpError(message, response, metadata)
    if (
      response.status !== 403 ||
      Number(response.headers.get("content-length")) > MAX_ERROR_BODY_BYTES
    ) {
      return error
    }

    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      const bodyReader = response.clone().body?.getReader()
      if (!bodyReader) return error
      reader = bodyReader
      const readBody = async (): Promise<unknown> => {
        const decoder = new TextDecoder()
        let body = ""
        let bytes = 0
        while (true) {
          const { done, value } = await bodyReader.read()
          if (done) return JSON.parse(body + decoder.decode())
          bytes += value.byteLength
          if (bytes > MAX_ERROR_BODY_BYTES) return null
          body += decoder.decode(value, { stream: true })
        }
      }
      const payload = await Promise.race([
        readBody(),
        new Promise<null>((resolve) => {
          timeout = setTimeout(() => resolve(null), ERROR_BODY_TIMEOUT_MS)
        }),
      ])
      if (
        !payload ||
        typeof payload !== "object" ||
        Array.isArray(payload) ||
        !("detail" in payload)
      ) {
        return error
      }
      const detail = payload.detail
      if (
        detail &&
        typeof detail === "object" &&
        !Array.isArray(detail) &&
        "code" in detail &&
        detail.code === MISALIGNMENT_ERROR_CODE
      ) {
        error.code = MISALIGNMENT_ERROR_CODE
      }
    } catch {
      // Error responses may come from proxies or have unreadable bodies.
    } finally {
      if (timeout !== undefined) clearTimeout(timeout)
      void reader?.cancel().catch(() => {})
    }
    return error
  }

  static fromPossibleFrameSafeError(error: unknown): HttpError | null {
    if (error instanceof HttpError) {
      return error
    }

    if (
      error &&
      typeof error === "object" &&
      FRAME_SAFE_ERROR_KEY in error &&
      error[FRAME_SAFE_ERROR_KEY] === "HttpError"
    ) {
      const safeError = error as FrameSafeHttpError
      const parsedError = new HttpError(
        safeError.message,
        {
          status: safeError.status,
          statusText: safeError.statusText,
        },
        safeError.metadata,
      )
      parsedError.stack = safeError.stack
      if (safeError.status === 403 && safeError.code === MISALIGNMENT_ERROR_CODE) {
        parsedError.code = MISALIGNMENT_ERROR_CODE
      }
      return parsedError
    }

    return null
  }
}

// does not extend Error so that it serializes correctly over structured clone
export class FrameSafeHttpError {
  [FRAME_SAFE_ERROR_KEY] = "HttpError"
  message: string
  stack?: string
  status?: number
  statusText?: string
  metadata?: ChatKitResponseMetadata
  declare code?: typeof MISALIGNMENT_ERROR_CODE

  constructor(
    message: string,
    res: Response | { status?: number; statusText?: string },
    metadata?: ChatKitResponseMetadata,
  ) {
    this.message = message
    this.stack = new Error(message).stack
    this.status = res.status
    this.statusText = res.statusText
    this.metadata = metadata
  }

  static fromHttpError(error: HttpError): FrameSafeHttpError {
    const frameSafeError = new FrameSafeHttpError(
      error.message,
      {
        status: error.status,
        statusText: error.statusText,
      },
      error.metadata,
    )
    if (error.status === 403 && error.code === MISALIGNMENT_ERROR_CODE) {
      frameSafeError.code = MISALIGNMENT_ERROR_CODE
    }
    return frameSafeError
  }
}
